import type {WellAllocation} from "@ots-frontend/ots-data";
import {
  OtsDataTable,
  OtsDataTableBody,
  OtsDataTableCell,
  OtsDataTableHead,
  OtsDataTableHeaderCell,
  OtsDataTableRow,
  OtsDataTableRowHeader,
} from "../components/OtsDataTable";

export type WellAllocationTableProps = {
  allocations: WellAllocation[];
  volumeUnit?: string;
  timeZone?: string;
  advanced?: boolean;
  compact?: boolean;
};

const quantity = new Intl.NumberFormat(undefined, {maximumFractionDigits: 2});
const percent = new Intl.NumberFormat(undefined, {style: "percent", maximumFractionDigits: 2});

/** Reconciliation table for the values that feed the allocation percentage. */
const bucketDate = (dttm: number, timeZone?: string) => new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  timeZone,
}).format(dttm);

export const WellAllocationTable = ({allocations, volumeUnit, timeZone, advanced = false, compact = false}: WellAllocationTableProps) => (
  <OtsDataTable density={compact ? "compact" : "default"}>
      <OtsDataTableHead>
        <tr>
          <OtsDataTableHeaderCell scope="col">Eligible well</OtsDataTableHeaderCell>
          {advanced && <OtsDataTableHeaderCell scope="col">Bucket totals</OtsDataTableHeaderCell>}
          <OtsDataTableHeaderCell scope="col" align="right">Window total</OtsDataTableHeaderCell>
          <OtsDataTableHeaderCell scope="col" align="right">Used in split</OtsDataTableHeaderCell>
          {advanced && <OtsDataTableHeaderCell scope="col" align="right">Average</OtsDataTableHeaderCell>}
          <OtsDataTableHeaderCell scope="col" align="right">Coverage</OtsDataTableHeaderCell>
          <OtsDataTableHeaderCell scope="col" align="right">Allocation</OtsDataTableHeaderCell>
        </tr>
      </OtsDataTableHead>
      <OtsDataTableBody>
        {allocations.map((allocation) => (
          <OtsDataTableRow key={allocation.wellId} className={allocation.allocation === 0 ? "text-[var(--ots-text-muted)]" : undefined}>
            <OtsDataTableRowHeader>
              <div>{allocation.wellname}</div>
              {allocation.allocation === 0 && <span className={["font-normal text-[var(--ots-text-muted)]", compact ? "ml-1.5 inline text-[10px]" : "mt-0.5 block text-xs"].join(" ")}>0% · still eligible</span>}
            </OtsDataTableRowHeader>
            {advanced && (
              <OtsDataTableCell className="min-w-56">
                {allocation.productionVolumes.length ? (
                  <div className={compact ? "flex flex-wrap gap-1" : "flex flex-wrap gap-1.5"}>
                    {allocation.productionVolumes.map((bucket) => (
                      <span key={`${bucket.dttm}-${bucket.id}`} className={["whitespace-nowrap rounded-md bg-[var(--ots-neutral-container)] tabular-nums text-[var(--ots-on-neutral-container)]", compact ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-1 text-xs"].join(" ")}>
                        {bucketDate(bucket.dttm, timeZone)} · {quantity.format(bucket.quantity)}
                      </span>
                    ))}
                  </div>
                ) : <span className="text-xs italic">No production reported</span>}
              </OtsDataTableCell>
            )}
            <OtsDataTableCell align="right" className="tabular-nums">{quantity.format(allocation.volume)}{volumeUnit ? ` ${volumeUnit}` : ""}</OtsDataTableCell>
            <OtsDataTableCell align="right" className="tabular-nums">
              {quantity.format(allocation.adjustedVolume)}{volumeUnit ? ` ${volumeUnit}` : ""}
              {allocation.adjustedVolume !== allocation.volume && <div className="text-xs text-[var(--ots-warning)]">normalized</div>}
            </OtsDataTableCell>
            {advanced && <OtsDataTableCell align="right" className="tabular-nums">{quantity.format(allocation.average)}{volumeUnit ? ` ${volumeUnit}` : ""}</OtsDataTableCell>}
            <OtsDataTableCell align="right" className="tabular-nums">{allocation.observedChunkCount} / {allocation.expectedChunkCount}</OtsDataTableCell>
            <OtsDataTableCell align="right" className={["font-bold tabular-nums text-[var(--ots-primary)]", compact ? "text-sm" : "text-base"].join(" ")}>{percent.format(allocation.allocation)}</OtsDataTableCell>
          </OtsDataTableRow>
        ))}
      </OtsDataTableBody>
  </OtsDataTable>
);
